import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { HierarchicalItem, HierarchyFormProps } from "../../../types/uiTypes";
import { formatDateTime } from "../../../utils";
import { ChildrenList } from "../ChildrenList";
import { FormButton } from "../FormButton";
import { FormFooter } from "../FormFooter";
import { FormField } from "../FormField";
import styles from "./Form.module.css";

export function HierarchyForm<T extends HierarchicalItem>({
  mode,
  item,
  parentItem,
  allItems,
  fields,
  validationSchema,
  onClose,
  onSave,
  onDelete,
  onNavigateToChild,
  onDirtyChange,
  onError,
  formHeader,
  formFooter,
  parentOptionLabel,
  readOnly = false,
  singularName,
}: HierarchyFormProps<T>) {
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Helper to build form values from field configs
  const buildFormValues = useCallback(() => {
    const values: Record<string, unknown> = {};
    fields.forEach(field => {
      if (field.name === "parentId") {
        values[field.name] =
          mode === "create" ? (parentItem?.id ?? null) : (item?.parentId ?? null);
      } else if (item && field.name in item) {
        values[field.name] = item[field.name as keyof T];
      } else if (field.defaultValue !== undefined) {
        values[field.name] = field.defaultValue;
      }
    });
    return values;
  }, [fields, item, parentItem, mode]);

  // Build default values from item
  const defaultValues = useMemo(() => buildFormValues(), [buildFormValues]);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    control,
    reset,
  } = useForm<Record<string, unknown>>({
    // @ts-expect-error - Zod schema type inference doesn't perfectly match react-hook-form's expected types
    resolver: zodResolver(validationSchema),
    mode: "onBlur",
    defaultValues,
  });

  // Reset form when item changes
  useEffect(() => {
    reset(buildFormValues());
  }, [item?.id, mode, parentItem?.id, reset, buildFormValues]);

  // Notify parent when dirty state changes
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  // Helper to normalize form data for API
  const normalizeFormData = useCallback(
    (data: Record<string, unknown>): Record<string, unknown> => {
      const normalized = { ...data };

      // Normalize code to lowercase
      if (normalized.code && typeof normalized.code === "string") {
        normalized.code = normalized.code.toLowerCase();
      }

      // Add parentId if missing (happens when select is disabled)
      if (mode === "create" && parentItem && !normalized.parentId) {
        normalized.parentId = parentItem.id;
      }

      // Handle null/undefined parentId
      if (normalized.parentId === null || normalized.parentId === undefined) {
        delete normalized.parentId;
      }

      return normalized;
    },
    [mode, parentItem],
  );

  const onSubmit = async (data: Record<string, unknown>) => {
    setSubmitting(true);
    setServerError(null);

    try {
      const normalized = normalizeFormData(data);
      await onSave(normalized);
      setSubmitting(false);
      setServerError(null);
    } catch (err) {
      setSubmitting(false);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setServerError(errorMessage);
      if (onError) {
        onError(err);
      }
    }
  };

  const isArchived = mode === "edit" && item && "status" in item && item.status === "archived";

  // Flatten items for parent selector (memoized)
  const itemOptions = useMemo(() => {
    const flattenItems = (itemList: T[], level = 0): Array<{ item: T; level: number }> => {
      const result: Array<{ item: T; level: number }> = [];
      for (const currentItem of itemList) {
        if (mode === "edit" && item && currentItem.id === item.id) {
          // Skip the current item when editing
          continue;
        }
        result.push({ item: currentItem, level });
        if (currentItem.children) {
          result.push(...flattenItems(currentItem.children as T[], level + 1));
        }
      }
      return result;
    };

    return flattenItems(allItems);
  }, [allItems, mode, item]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.modalForm}>
      {formHeader}
      {serverError && (
        <div className={styles.serverError} role="alert" aria-live="assertive">
          {serverError}
        </div>
      )}
      {fields.map(field => (
        <FormField
          key={field.name}
          field={field}
          register={register}
          errors={errors}
          watch={watch}
          control={control}
          mode={mode}
          parentItem={parentItem}
          itemOptions={itemOptions}
          parentOptionLabel={parentOptionLabel}
          disabled={isArchived || readOnly}
        />
      ))}

      {mode === "edit" && item && "updatedAt" in item && item.updatedAt ? (
        <div className={styles.metadata}>
          <span className={styles.metadataLabel}>Last Updated:</span>
          <span className={styles.metadataValue}>{formatDateTime(item.updatedAt as string)}</span>
        </div>
      ) : null}

      {mode === "edit" && item && onNavigateToChild && (
        <ChildrenList item={item} onNavigate={onNavigateToChild} />
      )}

      {formFooter ? (
        formFooter({
          modal: { type: mode, item, parentItem },
          onClose,
          onDelete,
          submitting,
          readOnly,
        })
      ) : (
        <FormFooter>
          <div>
            {mode === "edit" && onDelete && !isArchived && !readOnly && (
              <FormButton onClick={onDelete} variant="danger" disabled={submitting}>
                Delete
              </FormButton>
            )}
          </div>
          <div>
            <div style={{ display: "flex", gap: "12px" }}>
              <FormButton onClick={onClose} disabled={submitting}>
                {readOnly ? "Close" : "Cancel"}
              </FormButton>
              {!isArchived && !readOnly && (
                <FormButton type="submit" variant="primary" disabled={submitting}>
                  {submitting ? "Saving..." : mode === "create" ? "Create" : "Save"}
                </FormButton>
              )}
            </div>
          </div>
        </FormFooter>
      )}
    </form>
  );
}
